import {
  slaActivityHealthCounts,
  slaActivityInstancesByProcess,
  slaActivityTaskHealth,
  type SlaActivityTaskIdentity,
} from "model/slaActivitiesModel";
import {
  SLA_HEALTH_FILTER_ANY,
  type SlaHealth,
  type SlaHealthFilterValue,
} from "model/slaModel";
import React, { useMemo } from "react";
import SlaHealthFilterChips from "./SlaHealthFilterChips";
import { useSlaActivities } from "./slaActivitiesContext";

/**
 * **Filtro per salute SLA nella pagina Le mie attivita'**: gli stessi chip del
 * Monitor (totale, totale SLA, in tempo, a rischio, violati,
 * `SlaHealthFilterChips`) sui task ToDo/Doing gia' filtrati dagli altri filtri
 * della pagina.
 *
 * Regole:
 * - si mostra quando lo SLA dell'organizzazione e' attivo: `slaEnabled` del
 *   provider (impostazioni `enabled`, lo stesso segnale dei chip del Monitor,
 *   anche con zero istanze aperte) oppure `slaInUse` (almeno un'istanza letta),
 *   che resta l'unico segnale per i non-admin perche' le impostazioni sono
 *   admin-only. Senza SLA i task passano invariati e il filtro selezionato
 *   viene ignorato;
 * - la salute di ogni task e' quella del badge sulla card: istanza che lo
 *   governa + countdown locale (`slaActivityTaskHealth`);
 * - "Totale SLA" (`SLA_HEALTH_FILTER_ANY`) tiene i task con una salute
 *   dichiarata, cioe' quelli che portano un badge SLA, qualunque essa sia;
 * - filtro e conteggi sono **lato client** sulle liste in memoria: oggi la
 *   pagina riceve tutti i task (`PAGE_SIZE = 0` in `useActivities`). Con la
 *   paginazione server-side il filtro andra' spostato sul Server (nessun
 *   parametro di salute esiste oggi su `/tasks/canget` e `/tasks/doing`);
 * - le istanze sono quelle lette dal provider (prima pagina,
 *   `SLA_ACTIVITIES_INSTANCES_LIMIT`): un task la cui istanza non e' fra quelle
 *   lette resta senza SLA e conta solo nel totale (limite dichiarato, non
 *   mascherato).
 *
 * Render prop: il chiamante riceve le liste filtrate e il nodo dei chip
 * (`null` quando non si mostra) e li colloca nel proprio layout.
 */
export interface SlaActivitiesHealthFilterTask extends SlaActivityTaskIdentity {
  idprocesstoken: number;
}

interface SlaActivitiesHealthFilterProps<T extends SlaActivitiesHealthFilterTask> {
  todo: T[];
  doing: T[];
  selectedHealth: SlaHealthFilterValue | null;
  onSelectHealth: (health: SlaHealthFilterValue | null) => void;
  children: (view: { todo: T[]; doing: T[]; filter: React.ReactNode }) => React.ReactNode;
}

/** Il task passa il filtro scelto: "solo con SLA" = qualunque salute dichiarata. */
const matchesFilter = (
  health: SlaHealth | null,
  selected: SlaHealthFilterValue,
): boolean =>
  selected === SLA_HEALTH_FILTER_ANY ? health !== null : health === selected;

/** Chiave stabile delle righe che passano il filtro: evita liste nuove a ogni tick. */
const tokensKey = (tokens: number[]): string => tokens.join(",");

function SlaActivitiesHealthFilter<T extends SlaActivitiesHealthFilterTask>({
  todo,
  doing,
  selectedHealth,
  onSelectHealth,
  children,
}: SlaActivitiesHealthFilterProps<T>): React.ReactElement {
  const context = useSlaActivities();
  const active = Boolean(context?.slaEnabled || context?.slaInUse);
  const instances = context?.state.instances;
  const nowMs = context?.nowMs ?? 0;

  const byProcess = useMemo(
    () => slaActivityInstancesByProcess(active && instances ? instances : []),
    [active, instances],
  );

  // Saluti ricalcolate a ogni tick dell'orologio del provider (come i badge).
  const { counts, todoKey, doingKey } = useMemo(() => {
    if (!active) return { counts: null, todoKey: "", doingKey: "" };
    const todoMatches: number[] = [];
    const doingMatches: number[] = [];
    const healths: Array<SlaHealth | null> = [];
    const collect = (tasks: T[], matches: number[]) =>
      tasks.forEach((task) => {
        const health = slaActivityTaskHealth(byProcess, task, nowMs);
        healths.push(health);
        if (selectedHealth !== null && matchesFilter(health, selectedHealth)) {
          matches.push(task.idprocesstoken);
        }
      });
    collect(todo, todoMatches);
    collect(doing, doingMatches);
    return {
      counts: slaActivityHealthCounts(healths),
      todoKey: tokensKey(todoMatches),
      doingKey: tokensKey(doingMatches),
    };
  }, [active, byProcess, todo, doing, nowMs, selectedHealth]);

  const filtering = active && selectedHealth !== null;

  const filteredTodo = useMemo(() => {
    if (!filtering) return todo;
    const keep = new Set(todoKey ? todoKey.split(",").map(Number) : []);
    return todo.filter((task) => keep.has(task.idprocesstoken));
  }, [filtering, todo, todoKey]);

  const filteredDoing = useMemo(() => {
    if (!filtering) return doing;
    const keep = new Set(doingKey ? doingKey.split(",").map(Number) : []);
    return doing.filter((task) => keep.has(task.idprocesstoken));
  }, [filtering, doing, doingKey]);

  const filter = active ? (
    <SlaHealthFilterChips
      counts={counts}
      selectedHealth={selectedHealth}
      onSelectHealth={(health) => onSelectHealth(health as SlaHealthFilterValue | null)}
      testIdPrefix="sla-activities-health-filter"
    />
  ) : null;

  return <>{children({ todo: filteredTodo, doing: filteredDoing, filter })}</>;
}

export default SlaActivitiesHealthFilter;
